/**
 * ResourceApiService (Angular 18) — HTTP wrappers for the
 * /api/resource(s), /api/equipment, /api/facilities and resource availability
 * / assignment domains.
 *
 * Replaces the AngularJS legacy service at:
 *   apps/b2b-ui/common/services/api/resourceApi/resourceApi.service.js
 *
 * Exposed as `apiService.resource.*` through the ApiService facade.
 *
 * Legacy quirks preserved 1:1:
 *  - Top-level `list`/`templates`/`create`/`update` use v2; `detail`/`remove`
 *    stay on v1 (matches legacy `this.service.replace(/v1/, 'v2')` + paths).
 *  - `templates` and `templatesSummaries` hit `/v2/api/resourcetemplates*`
 *    (the legacy concatenated `this.service + 'templates'` with no `/`, so
 *    `/v1/api/resource` + `templates` becomes `/v1/api/resourcetemplates`,
 *    then v1→v2). Preserved verbatim — that is the URL the backend serves.
 *  - `instances.detail` uses v1; the rest of `instances.*` uses v2.
 *  - `instances.batchCreate` stays on v1 (legacy never swapped to v2).
 *  - `equipment.create`/`facilities.create` stay on v1; the corresponding
 *    `list` switches to v2 (legacy parity).
 *  - `programs.list`, `field.*` and `available.event` reject with
 *    `{ data: { error: 'Resource ID is required' } }` when the required id is
 *    missing — preserves the legacy `$q.defer().reject(...)` shape.
 *  - `availability.detail` requires `entityId`; legacy used a global `q` due
 *    to a missing `var`. Here we always build params locally.
 *  - `assignment.event` builds `?resourceId=:id` when assigning, otherwise
 *    `?assign=false`. The body forwards type/resourceTemplateId/resourceId/
 *    fields/quantity/resourceCategory.
 */
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AjsResponse, wrapAjs } from '../ajs-response';
import { buildParams, PAGINATION_SEARCH_FIELDS } from '../http-params.util';

// ============= Base URLs =============
// Hardcoded for grep-ability: pasting a URL from the browser's Network tab
// into a code search will land you here.

const RESOURCE_BASE_V1 = '/v1/api/resource';
const RESOURCE_TYPE_BASE_V1 = '/v1/api/resource/type';
const RESOURCE_TYPE_BASE_V2 = '/v2/api/resource/type';
const RESOURCE_TEMPLATES_BASE_V2 = '/v2/api/resourcetemplates';
const RESOURCE_INSTANCES_BASE_V1 = '/v1/api/resource/instances';
const RESOURCE_INSTANCES_BASE_V2 = '/v2/api/resource/instances';
const RESOURCE_METADATA_BASE_V2 = '/v2/api/resource/metadata';
const EQUIPMENT_BASE_V1 = '/v1/api/equipment';
const EQUIPMENT_BASE_V2 = '/v2/api/equipment';
const FACILITIES_BASE_V1 = '/v1/api/facilities';
const FACILITIES_BASE_V2 = '/v2/api/facilities';
const RESOURCE_REQUESTS_BASE_V2 = '/v2/api/resource/requests';
const AVAILABILITY_BASE_V2 = '/v2/api/resources/availability/configurations';
const RESOURCES_AVAILABLE_EVENT_BASE_V1 = '/v1/api/resources/available/event';
const RESOURCES_ASSIGNMENT_EVENT_BASE_V1 = '/v1/api/resources/assignment/event';
const RESOURCES_ASSIGNMENT_REQUEST_BASE_V2 = '/v2/api/resources/assignment/request';

const RESOURCE_LIST_FIELDS = ['resourceCategory', ...PAGINATION_SEARCH_FIELDS] as const;

const RESOURCE_TEMPLATES_SUMMARY_FIELDS = ['resourceCategory', ...PAGINATION_SEARCH_FIELDS, 'formId'] as const;

const REQUESTS_LIST_FIELDS = [
  ...PAGINATION_SEARCH_FIELDS,
  'eventId',
  'ownerId',
  'resourceStartTime',
  'resourceEndTime',
  'eventTemplateId',
] as const;

const INSTANCES_LIST_FIELDS = ['resourceTemplateId', ...PAGINATION_SEARCH_FIELDS] as const;
const INSTANCES_OWNER_LIST_FIELDS = ['ownerId', ...PAGINATION_SEARCH_FIELDS] as const;
const INSTANCES_FILTER_LIST_FIELDS = ['category', ...PAGINATION_SEARCH_FIELDS] as const;

const ASSIGNMENT_REQUEST_FIELDS = [
  'match',
  'available',
  ...PAGINATION_SEARCH_FIELDS,
  'resourceOwnerId',
  'startTime',
  'durationHour',
  'durationMinutes',
] as const;

const ASSIGNMENT_RESOURCE_REQUEST_FIELDS = [
  'match',
  'available',
  'resourceTemplateId',
  'resourceCategory',
  'startTime',
  'durationHour',
  'durationMinutes',
  ...PAGINATION_SEARCH_FIELDS,
] as const;

const ASSIGNMENT_PRIVATE_EVENT_FIELDS = [
  'match',
  'available',
  'eventId',
  'eventTemplateId',
  'scheduleId',
  'resourceTemplateId',
  'resourceCategory',
  'startTime',
  'durationHour',
  'durationMinutes',
  ...PAGINATION_SEARCH_FIELDS,
] as const;

// ============= Helpers =============

/**
 * Mirrors the legacy `rejection()` helper used by `programs`, `field`,
 * `available` and `assignment` sub-namespaces when a required id is missing.
 *
 * Legacy used `$q.defer().reject({ data: { error: 'Resource ID is required' } })`
 * after a 100ms `$timeout`. We use a plain `Promise.reject(...)` here — the
 * shape is identical from the consumer's `.catch(err => err.data.error)`
 * perspective.
 */
function missingIdRejection<T>(): Promise<AjsResponse<T>> {
  return Promise.reject({ data: { error: 'Resource ID is required' } });
}

interface FieldProps {
  name: string;
  type?: unknown;
  fieldOrder?: unknown;
  stringValue?: unknown;
  stringArrayValue?: unknown;
  minValue?: unknown;
  maxValue?: unknown;
  hidden?: unknown;
}

function buildFieldBody(props: FieldProps) {
  return {
    fieldOrder: props.fieldOrder,
    name: props.name,
    type: props.type,
    stringValue: props.stringValue,
    stringArrayValue: props.stringArrayValue,
    minValue: props.minValue,
    maxValue: props.maxValue,
    hidden: props.hidden,
  };
}

interface AvailabilityProps {
  id?: number | string;
  entityId: number | string;
  resourceCategory?: unknown;
  available?: unknown;
  unavailable?: unknown;
}

interface AssignmentEventProps {
  id?: number | string;
  assign?: boolean;
  type?: unknown;
  resourceTemplateId?: unknown;
  resourceId?: unknown;
  fields?: unknown;
  quantity?: unknown;
  resourceCategory?: unknown;
}

interface AvailableEventProps {
  type?: unknown;
  resourceTemplateId?: unknown;
  resourceId?: unknown;
  fields?: unknown;
  quantity?: unknown;
  resourceCategory?: unknown;
}

interface AssignmentRequestActionProps {
  resourceRequestId: number | string;
  resourceId?: unknown;
  resourceOwnerId?: number | string;
  sendEmail?: unknown;
}

// ============= Service =============

@Injectable({ providedIn: 'root' })
export class ResourceApiService {
  private readonly http = inject(HttpClient);

  // ─── Top-level methods ─────────────────────────────────────────────

  /**
   * GET /v2/api/resource/type/summaries
   *
   * Legacy accepts `props` as an object with `resourceCategory` plus pagination.
   * Some callers pass a plain string (e.g. `list('Staff')`) which the legacy
   * QueryStringBuilderFactory silently turned into no params — preserved here
   * because `buildParams` skips keys not in the whitelist.
   */
  list(props?: unknown): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${RESOURCE_TYPE_BASE_V2}/summaries`, {
        params: buildParams(props as Record<string, unknown> | undefined, RESOURCE_LIST_FIELDS),
      }),
    );
  }

  /** GET /v2/api/resourcetemplates */
  templates(props?: Record<string, unknown>): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(RESOURCE_TEMPLATES_BASE_V2, {
        params: buildParams(props, RESOURCE_TEMPLATES_SUMMARY_FIELDS),
      }),
    );
  }

  /** GET /v2/api/resourcetemplates/summary */
  templatesSummaries(props?: Record<string, unknown>): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${RESOURCE_TEMPLATES_BASE_V2}/summary`, {
        params: buildParams(props, RESOURCE_TEMPLATES_SUMMARY_FIELDS),
      }),
    );
  }

  /** POST /v2/api/resourcetemplates */
  create(props: {
    name: unknown;
    description: unknown;
    resourceCategory: unknown;
    fields: unknown;
  }): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(RESOURCE_TEMPLATES_BASE_V2, {
        name: props.name,
        description: props.description,
        resourceCategory: props.resourceCategory,
        fields: props.fields,
      }),
    );
  }

  /** DELETE /v1/api/resource/type/:id */
  remove(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.delete<unknown>(`${RESOURCE_TYPE_BASE_V1}/${id}`));
  }

  /** GET /v1/api/resource/type/:id */
  detail(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${RESOURCE_TYPE_BASE_V1}/${id}`));
  }

  /** PUT /v2/api/resource/type/:id */
  update(props: {
    id: number | string;
    name: unknown;
    description: unknown;
    resourceCategory: unknown;
    fields: unknown;
  }): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.put<unknown>(`${RESOURCE_TYPE_BASE_V2}/${props.id}`, {
        name: props.name,
        description: props.description,
        resourceCategory: props.resourceCategory,
        fields: props.fields,
      }),
    );
  }

  // ─── Metadata (online payment uuid) ────────────────────────────────

  readonly metadata = {
    get: (props: { entityType: string; entityId: string | number }): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${RESOURCE_METADATA_BASE_V2}/${props.entityType}/${props.entityId}`)),

    create: (props: { entityType: string; entityId: string | number }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(RESOURCE_METADATA_BASE_V2, {
          entityType: props.entityType,
          entityId: props.entityId,
        }),
      ),

    delete: (props: { onlinePaymentUUID: string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.delete<unknown>(`${RESOURCE_METADATA_BASE_V2}/${props.onlinePaymentUUID}`)),
  };

  // ─── Equipment ─────────────────────────────────────────────────────

  readonly equipment = {
    create: (props: { name: unknown; description: unknown }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(EQUIPMENT_BASE_V1, {
          name: props.name,
          description: props.description,
        }),
      ),
    list: (): Promise<AjsResponse<unknown>> => wrapAjs(this.http.get<unknown>(EQUIPMENT_BASE_V2)),
    remove: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.delete<unknown>(`${EQUIPMENT_BASE_V1}/${id}`)),
  };

  // ─── Facilities ────────────────────────────────────────────────────

  readonly facilities = {
    create: (props: { name: unknown; description: unknown; capacity: unknown }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(FACILITIES_BASE_V1, {
          name: props.name,
          description: props.description,
          capacity: props.capacity,
        }),
      ),
    list: (): Promise<AjsResponse<unknown>> => wrapAjs(this.http.get<unknown>(FACILITIES_BASE_V2)),
    remove: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.delete<unknown>(`${FACILITIES_BASE_V1}/${id}`)),
  };

  // ─── Requests ──────────────────────────────────────────────────────

  readonly requests = {
    list: (props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(RESOURCE_REQUESTS_BASE_V2, {
          params: buildParams(props, REQUESTS_LIST_FIELDS),
        }),
      ),
  };

  // ─── Instances ─────────────────────────────────────────────────────

  readonly instances = {
    create: (props: {
      name: unknown;
      fields: unknown;
      ownerId: unknown;
      description: unknown;
      resourceUser: unknown;
      resourceTemplateId: unknown;
      resourceTemplateName: unknown;
      resourceCategory: unknown;
    }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(RESOURCE_INSTANCES_BASE_V2, {
          name: props.name,
          fields: props.fields,
          ownerId: props.ownerId,
          description: props.description,
          resourceUser: props.resourceUser,
          resourceTemplateId: props.resourceTemplateId,
          resourceTemplateName: props.resourceTemplateName,
          resourceCategory: props.resourceCategory,
        }),
      ),

    batchCreate: (props: { resourceTemplateId: unknown; entityIds: unknown }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(`${RESOURCE_INSTANCES_BASE_V1}/batch`, {
          resourceTemplateId: props.resourceTemplateId,
          entityIds: props.entityIds,
        }),
      ),

    remove: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.delete<unknown>(`${RESOURCE_INSTANCES_BASE_V2}/${id}`)),

    /**
     * GET /v2/api/resource/instances
     *
     * Legacy: when `id` is provided it is folded into `props.resourceTemplateId`.
     * When called as `list(id)` without props the legacy crashed (`undefined.x`);
     * here we tolerate that case and send only `?resourceTemplateId=:id`.
     */
    list: (id: number | string | null | undefined, props?: Record<string, unknown>): Promise<AjsResponse<unknown>> => {
      const data: Record<string, unknown> = { ...(props ?? {}) };
      if (id != null) data['resourceTemplateId'] = id;
      return wrapAjs(
        this.http.get<unknown>(RESOURCE_INSTANCES_BASE_V2, {
          params: buildParams(data, INSTANCES_LIST_FIELDS),
        }),
      );
    },

    ownerList: (props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(RESOURCE_INSTANCES_BASE_V2, {
          params: buildParams(props, INSTANCES_OWNER_LIST_FIELDS),
        }),
      ),

    filterList: (props?: unknown): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(RESOURCE_INSTANCES_BASE_V2, {
          params: buildParams(
            // Legacy callers sometimes pass a plain string (e.g. `'Staff'`); buildParams
            // safely returns no params for unrecognized shapes via the whitelist.
            props as Record<string, unknown> | undefined,
            INSTANCES_FILTER_LIST_FIELDS,
          ),
        }),
      ),

    /** GET /v1/api/resource/instances/:id (legacy never swapped to v2). */
    detail: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${RESOURCE_INSTANCES_BASE_V1}/${id}`)),

    update: (props: { id: number | string } & Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.put<unknown>(`${RESOURCE_INSTANCES_BASE_V2}/${props.id}`, props)),
  };

  // ─── Programs (single read) ────────────────────────────────────────

  readonly programs = {
    /** GET /v1/api/resource/type/:id/programs/summary — rejects when !id. */
    list: (id: number | string | null | undefined): Promise<AjsResponse<unknown>> =>
      id == null
        ? missingIdRejection<unknown>()
        : wrapAjs(this.http.get<unknown>(`${RESOURCE_TYPE_BASE_V1}/${id}/programs/summary`)),
  };

  // ─── Field (resource template fields) ──────────────────────────────

  readonly field = {
    remove: (name: string, id: number | string | null | undefined): Promise<AjsResponse<unknown>> =>
      id == null
        ? missingIdRejection<unknown>()
        : wrapAjs(this.http.delete<unknown>(`${RESOURCE_TYPE_BASE_V1}/${id}/fields/${name}`)),

    update: (props: FieldProps, id: number | string | null | undefined): Promise<AjsResponse<unknown>> =>
      id == null
        ? missingIdRejection<unknown>()
        : wrapAjs(this.http.put<unknown>(`${RESOURCE_TYPE_BASE_V1}/${id}/fields/${props.name}`, buildFieldBody(props))),

    create: (props: FieldProps, id: number | string | null | undefined): Promise<AjsResponse<unknown>> =>
      id == null
        ? missingIdRejection<unknown>()
        : wrapAjs(this.http.post<unknown>(`${RESOURCE_TYPE_BASE_V1}/${id}/fields`, buildFieldBody(props))),
  };

  // ─── Available (event instance resource lookup) ────────────────────

  readonly available = {
    event: (props: AvailableEventProps, id: number | string | null | undefined): Promise<AjsResponse<unknown>> =>
      id == null
        ? missingIdRejection<unknown>()
        : wrapAjs(
            this.http.post<unknown>(`${RESOURCES_AVAILABLE_EVENT_BASE_V1}/${id}`, {
              type: props.type,
              resourceTemplateId: props.resourceTemplateId,
              resourceId: props.resourceId,
              fields: props.fields,
              quantity: props.quantity,
              resourceCategory: props.resourceCategory,
            }),
          ),
  };

  // ─── Availability (v2 configurations) ──────────────────────────────

  readonly availability = {
    create: (props: AvailabilityProps): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(AVAILABILITY_BASE_V2, {
          entityId: props.entityId,
          resourceCategory: props.resourceCategory,
          available: props.available,
          unavailable: props.unavailable,
        }),
      ),

    update: (props: AvailabilityProps): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.put<unknown>(AVAILABILITY_BASE_V2, {
          id: props.id,
          entityId: props.entityId,
          resourceCategory: props.resourceCategory,
          available: props.available,
          unavailable: props.unavailable,
        }),
      ),

    detail: (props: { entityId: number | string }): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(AVAILABILITY_BASE_V2, {
          params: buildParams({ entityId: props.entityId }, ['entityId']),
        }),
      ),
  };

  // ─── Assignment ────────────────────────────────────────────────────

  readonly assignment = {
    /**
     * POST /v1/api/resources/assignment/event/:eventId
     *
     * When `props.assign` is `true` the URL gains `?resourceId=:id` (and a
     * missing `id` rejects). When `props.assign` is falsy the URL gains
     * `?assign=false` instead.
     */
    event: (
      props: AssignmentEventProps,
      eventId: number | string | null | undefined,
    ): Promise<AjsResponse<unknown>> => {
      if (eventId == null) return missingIdRejection<unknown>();
      if (props.assign === true && !Object.prototype.hasOwnProperty.call(props, 'id')) {
        return missingIdRejection<unknown>();
      }
      const params = props.assign
        ? new HttpParams().set('resourceId', String(props.id))
        : new HttpParams().set('assign', 'false');
      return wrapAjs(
        this.http.post<unknown>(
          `${RESOURCES_ASSIGNMENT_EVENT_BASE_V1}/${eventId}`,
          {
            type: props.type,
            resourceTemplateId: props.resourceTemplateId,
            resourceId: props.resourceId,
            fields: props.fields,
            quantity: props.quantity,
            resourceCategory: props.resourceCategory,
          },
          { params },
        ),
      );
    },

    /** GET /v2/api/resources/assignment/request/:id */
    request: (props: { id: number | string } & Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${RESOURCES_ASSIGNMENT_REQUEST_BASE_V2}/${props.id}`, {
          params: buildParams(props, ASSIGNMENT_REQUEST_FIELDS),
        }),
      ),

    /** GET /v2/api/resources/assignment/request/private/:resourceRequestId */
    resourceRequestPrivateEvent: (
      props: { resourceRequestId: number | string } & Record<string, unknown>,
    ): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${RESOURCES_ASSIGNMENT_REQUEST_BASE_V2}/private/${props.resourceRequestId}`, {
          params: buildParams(props, ASSIGNMENT_PRIVATE_EVENT_FIELDS),
        }),
      ),

    /** GET /v2/api/resources/assignment/request */
    resourceRequest: (props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(RESOURCES_ASSIGNMENT_REQUEST_BASE_V2, {
          params: buildParams(props, ASSIGNMENT_RESOURCE_REQUEST_FIELDS),
        }),
      ),

    /** POST /v2/api/resources/assignment/request/:resourceRequestId — assign=true */
    assign: (props: AssignmentRequestActionProps): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(
          `${RESOURCES_ASSIGNMENT_REQUEST_BASE_V2}/${props.resourceRequestId}`,
          {
            resourceId: props.resourceId,
            assign: true,
            sendEmail: props.sendEmail,
          },
          {
            params: buildParams(props as unknown as Record<string, unknown>, ['resourceOwnerId']),
          },
        ),
      ),

    /** POST /v2/api/resources/assignment/request/:resourceRequestId — assign=false */
    unassign: (props: AssignmentRequestActionProps): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.post<unknown>(
          `${RESOURCES_ASSIGNMENT_REQUEST_BASE_V2}/${props.resourceRequestId}`,
          {
            resourceId: props.resourceId,
            assign: false,
            sendEmail: props.sendEmail,
          },
          {
            params: buildParams(props as unknown as Record<string, unknown>, ['resourceOwnerId']),
          },
        ),
      ),
  };
}
